<template>
  <div class="w-full bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Category Filter Container -->
      <div class="relative py-4">
        <!-- Scroll Container -->
        <div
          ref="scrollContainer"
          class="flex space-x-3 overflow-x-auto scrollbar-hide scroll-smooth"
          @scroll="handleScroll"
        >
          <!-- All Categories Button -->
          <button
            @click="selectCategory('All')"
            class="flex-shrink-0 px-6 py-3 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap"
            :class="[
              selectedCategory === 'All' || selectedCategory === null
                ? 'bg-purple-600 text-white shadow-lg transform scale-105'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
            ]"
          >
            All
          </button>

          <!-- Category Buttons -->
          <button
            v-for="category in categories"
            :key="category"
            @click="selectCategory(category)"
            class="flex-shrink-0 px-6 py-3 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap"
            :class="[
              selectedCategory === category
                ? 'bg-purple-600 text-white shadow-lg transform scale-105'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
            ]"
          >
            {{ category }}
          </button>
        </div>

        <!-- Scroll Indicators -->
        <div
          v-if="showLeftIndicator"
          class="absolute left-0 top-1/2 transform -translate-y-1/2 bg-gradient-to-r from-white dark:from-gray-900 to-transparent w-8 h-full flex items-center justify-start pointer-events-none"
        >
          <ChevronLeftIcon class="w-5 h-5 text-gray-400" />
        </div>
        
        <div
          v-if="showRightIndicator"
          class="absolute right-0 top-1/2 transform -translate-y-1/2 bg-gradient-to-l from-white dark:from-gray-900 to-transparent w-8 h-full flex items-center justify-end pointer-events-none"
        >
          <ChevronRightIcon class="w-5 h-5 text-gray-400" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/vue/24/outline';

export default {
  name: 'CategoryFilter',
  
  components: {
    ChevronLeftIcon,
    ChevronRightIcon
  },
  
  props: {
    selectedCategory: {
      type: String,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    categories: {
      type: Array,
      default: () => []
    }
  },
  
  emits: ['categorySelect'],
  
  data() {
    return {
      showLeftIndicator: false,
      showRightIndicator: false
    };
  },
  
  methods: {
    selectCategory(category) {
      const newCategory = this.selectedCategory === category ? null : category;
      this.$emit('categorySelect', newCategory);
      this.scrollSelectedCategoryIntoView(category);
    },
    
    scrollSelectedCategoryIntoView(category) {
      this.$nextTick(() => {
        if (!this.$refs.scrollContainer) return;
        
        const buttons = Array.from(this.$refs.scrollContainer.querySelectorAll('button'));
        const button = buttons.find(btn => btn.textContent.trim() === category);
        
        if (button) {
          button.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center'
          });
        }
      });
    },
    
    handleScroll() {
      this.updateScrollIndicators();
    },
    
    updateScrollIndicators() {
      if (!this.$refs.scrollContainer) return;
      
      const { scrollLeft, scrollWidth, clientWidth } = this.$refs.scrollContainer;
      this.showLeftIndicator = scrollLeft > 10;
      this.showRightIndicator = scrollLeft < scrollWidth - clientWidth - 10;
    }
  },
  
  mounted() {
    this.$nextTick(() => {
      this.updateScrollIndicators();
      
      if (this.selectedCategory && this.selectedCategory !== 'All') {
        this.scrollSelectedCategoryIntoView(this.selectedCategory);
      }
    });
    
    window.addEventListener('resize', this.updateScrollIndicators);
  },
  
  beforeUnmount() {
    window.removeEventListener('resize', this.updateScrollIndicators);
  }
};
</script>

<style scoped>
/* Hide scrollbar for webkit browsers */
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}

/* Hide scrollbar for Firefox */
.scrollbar-hide {
  -ms-overflow-style: none;  /* Internet Explorer 10+ */
  scrollbar-width: none;  /* Firefox */
}

/* Smooth scroll behavior */
.scroll-smooth {
  scroll-behavior: smooth;
}

/* Ensure buttons don't shrink */
button {
  flex-shrink: 0;
}
</style>